/* sb-dc-session.js — give every visit an id, so the funnel can be counted in
   people instead of events. 2026-09-04.

   Today's numbers say 243 editors opened, 312 templates applied, 16 add
   attempts, 2 orders — but not how many *visitors* that was. One person trying
   six templates looks the same as six people trying one. Without that, there is
   no way to tell whether any of this week's fixes moved anything.

   What it does: mints one id per browser tab session, stamps it onto every row
   the design center writes to dc_events, and records the moments the app does
   not log itself — the session's first page view, and the design id that ends
   up on the order.

   Why a wrapper: the events are written from inside sb-dc-aug.js, which is far
   too large to re-upload through the theme API. So the insert is caught on its
   way out of the browser and the sid is added to the row, the same technique
   sb-dc-cartprops.js uses on /cart/add.js.

   sessionStorage, not localStorage, and no cookie: this is a per-visit
   analytics id, not a person. Close the tab and it is gone; nothing about the
   visitor is stored or sent, and the id means nothing outside dc_events.

   Joining to an order: a design center order carries the print file URL, which
   contains the design id (dsn_...). The cart_link row written here pairs that
   id with the sid, so an order can be traced back to the visit that made it.

   2026-09-10 — this file also carries the dc_templates paging fix. It is a
   lodger, and the reason is load order: see the block below.

   Diagnostics: window.__dcSession, window.__dcTplPaging. */
(function () {
  'use strict';

  var KEY = 'sbdc_sid';
  var EVENTS_RE = /\/rest\/v1\/dc_events(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var TEMPLATES_RE = /\/rest\/v1\/dc_templates(\?|$)/;

  function mint() {
    var rnd = 'xxxxxxxx'.replace(/x/g, function () {
      return Math.floor(Math.random() * 36).toString(36);
    });
    return 's_' + Date.now().toString(36) + '_' + rnd;
  }

  var sid;
  try {
    sid = sessionStorage.getItem(KEY);
    if (!sid) { sid = mint(); sessionStorage.setItem(KEY, sid); }
  } catch (e) {
    sid = mint();   // private mode, storage disabled - still useful within the page
  }

  var stats = { sid: sid, stamped: 0, logged: 0 };
  window.__dcSession = stats;
  window.__dcSid = function () { return sid; };

  function project() { return window.__dcSupabase || null; }

  /* Our own writes go through the plain fetch we captured first, so they do not
     recurse through the wrapper below. */
  var rawFetch = window.fetch.bind(window);

  function log(kind, ref, meta) {
    var S = project();
    if (!S) return;
    try {
      rawFetch(S.url + '/rest/v1/dc_events', {
        method: 'POST',
        headers: {
          apikey: S.anonKey, Authorization: 'Bearer ' + S.anonKey,
          'Content-Type': 'application/json', Prefer: 'return=minimal'
        },
        body: JSON.stringify({ kind: kind, ref: ref || null, meta: meta || null, sid: sid })
      }).then(function () { stats.logged++; }, function () {});
    } catch (e) {}
  }

  /* Add the sid to whatever the app is inserting, whether it sends one row or
     an array of them. */
  function stamp(data) {
    if (Array.isArray(data)) { data.forEach(stamp); return data; }
    if (data && typeof data === 'object') {
      if (!data.sid) { data.sid = sid; stats.stamped++; }
    }
    return data;
  }

  /* The design id lives in the print file URL: .../dc-prints/dsn_xxx/front.png */
  function designIdFrom(props) {
    if (!props) return null;
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var v = String(props[keys[i]] || '');
      var m = v.match(/dc-prints\/([A-Za-z0-9_-]+)\//);
      if (m) return m[1];
    }
    return null;
  }

  /* ---------- dc_templates paging (2026-09-10) ----------------------------

     PostgREST answers at most 1,000 rows. dc_templates passed that on the way
     to 1,578, and both readers ask for the whole table in one go:

       /pages/design-templates   the section's own load
       sb-dc-tplprod.js          id + products, to filter the panel in the app

     So both were seeing the first thousand rows and nothing after. Measured on
     the live theme: the sidebar counted "All Templates 1000" — the cap, to the
     row. Every one of the 131 active step & repeat designs sits past it, under
     any ordering, because they are the oldest set in the table; the customer
     saw a category with nothing in it. On the app side the truncation is worse
     than invisible: a row missing from the products map reads as untagged, and
     with NAME_GUESS off an untagged design shows on no product at all.

     The fix pages through instead, and lives here because of WHEN it has to
     run. This is the only script on the templates page that is neither defer
     nor async, so it is the one thing guaranteed to be in place before the
     section's inline code fetches. On the design center page it loads ahead of
     tplprod for the same reason. Moving it into a file of its own means adding
     a synchronous script tag to sections/sb-dc-templates.liquid (58 KB, Korean
     comments, has to be re-uploaded whole) — worth doing when that file is next
     touched for other reasons, not for this.

     Only plain GET string urls with no limit/offset of their own are touched,
     so a caller that already pages is left alone. Anything unexpected falls
     back to the original response: a truncated list beats a broken page.

     Ordering matters for paging. An order= already in the url is kept; without
     one PostgREST gives no stable order and offsets would drop and repeat
     rows, so id.asc is added and the walk restarts from zero. */
  var PAGE = 1000;
  var HARD_CAP = 50000;   // a runaway loop would hammer the API
  var paging = { calls: 0, paged: 0, rows: 0, last: null };
  window.__dcTplPaging = function () { return paging; };

  function methodOf(input, init) {
    return String((init && init.method) || 'GET').toUpperCase();
  }

  function fetchAll(orig, url, init) {
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    var ordered = /[?&]order=/.test(url) ? url : url + sep + 'order=id.asc';
    var sep2 = ordered.indexOf('?') > -1 ? '&' : '?';
    var all = [];

    function step() {
      return orig.call(window, ordered + sep2 + 'limit=' + PAGE + '&offset=' + all.length, init)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (batch) {
          if (!Array.isArray(batch) || !batch.length) return;
          all = all.concat(batch);
          if (batch.length === PAGE && all.length < HARD_CAP) return step();
        });
    }

    return step().then(function () {
      paging.paged++;
      paging.rows = all.length;
      paging.last = all.length;
      return new Response(JSON.stringify(all), {
        status: 200, headers: { 'Content-Type': 'application/json' }
      });
    });
  }

  var orig = window.fetch;
  if (typeof orig === 'function' && !orig.__dcSidWrapped) {
    var wrapped = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var body = init && init.body;

        if (TEMPLATES_RE.test(url) && typeof input === 'string'
            && methodOf(input, init) === 'GET' && !/[?&](limit|offset)=/.test(url)) {
          paging.calls++;
          /* Ask once as the caller wrote it. Under a thousand rows came back,
             nothing was truncated and the original response is handed straight
             through - no second request, no reordering. */
          return orig.apply(this, [input, init]).then(function (r) {
            if (!r.ok) return r;
            return r.clone().json().then(function (rows) {
              if (!Array.isArray(rows) || rows.length < PAGE) return r;
              return fetchAll(orig, url, init).catch(function () { return r; });
            }).catch(function () { return r; });
          });
        }

        if (EVENTS_RE.test(url) && typeof body === 'string') {
          init = Object.assign({}, init, { body: JSON.stringify(stamp(JSON.parse(body))) });
        } else if (CART_ADD_RE.test(url) && typeof body === 'string') {
          var data = JSON.parse(body);
          var items = Array.isArray(data.items) ? data.items : [data];
          for (var i = 0; i < items.length; i++) {
            var id = designIdFrom(items[i] && items[i].properties);
            if (id) {
              log('cart_link', id, { design_id: id, variant: items[i].id || null });
              break;
            }
          }
        }
      } catch (e) {
        /* Analytics must never break an order or an event. */
      }
      return orig.apply(this, [input, init]);
    };
    wrapped.__dcSidWrapped = true;
    window.fetch = wrapped;
  }

  /* One row per visit, written once, so "how many people came" is a count of
     distinct sid rather than a guess from design_start. */
  try {
    if (!sessionStorage.getItem(KEY + '_seen')) {
      sessionStorage.setItem(KEY + '_seen', '1');
      log('session_start', null, {
        path: location.pathname,
        ref: document.referrer ? document.referrer.split('?')[0].slice(0, 120) : null,
        w: window.innerWidth, h: window.innerHeight
      });
    }
  } catch (e) {}
})();
